import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';

import './shared/i18n/config';

// Self-hosted fonts via @fontsource (loaded on app startup)
// Sans-serif fonts
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';
import '@fontsource/lato/400.css';
import '@fontsource/lato/700.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import '@fontsource/open-sans/400.css';
import '@fontsource/open-sans/600.css';
import '@fontsource/open-sans/700.css';
import '@fontsource/montserrat/400.css';
import '@fontsource/montserrat/500.css';
import '@fontsource/montserrat/600.css';
import '@fontsource/montserrat/700.css';
import '@fontsource/nunito/400.css';
import '@fontsource/nunito/600.css';
import '@fontsource/nunito/700.css';
import '@fontsource/quicksand/400.css';
import '@fontsource/quicksand/500.css';
import '@fontsource/quicksand/600.css';
import '@fontsource/quicksand/700.css';
// Serif fonts
import '@fontsource/playfair-display/400.css';
import '@fontsource/playfair-display/500.css';
import '@fontsource/playfair-display/600.css';
import '@fontsource/playfair-display/700.css';
import '@fontsource/merriweather/400.css';
import '@fontsource/merriweather/700.css';
// Handwriting fonts
import '@fontsource/caveat/400.css';
import '@fontsource/caveat/500.css';
import '@fontsource/caveat/600.css';
import '@fontsource/caveat/700.css';
import '@fontsource/pacifico/400.css';
import '@fontsource/dancing-script/400.css';
import '@fontsource/dancing-script/500.css';
import '@fontsource/dancing-script/600.css';
import '@fontsource/dancing-script/700.css';
// Monospace fonts
import '@fontsource/fira-code/400.css';
import '@fontsource/fira-code/500.css';
import '@fontsource/fira-code/600.css';
import '@fontsource/fira-code/700.css';
// Hebrew-optimized fonts
import '@fontsource/heebo/400.css';
import '@fontsource/heebo/500.css';
import '@fontsource/heebo/600.css';
import '@fontsource/heebo/700.css';
import '@fontsource/rubik/400.css';
import '@fontsource/rubik/500.css';
import '@fontsource/rubik/600.css';
import '@fontsource/rubik/700.css';
import '@fontsource/alef/400.css';
import '@fontsource/alef/700.css';
import '@fontsource/varela-round/400.css';
import '@fontsource/secular-one/400.css';

// Lazy: each entry point loads only its own tree. A static App import makes the print page
// download the whole application before it can fetch its own content.
const App = React.lazy(() => import('./App'));
const PresenterWindow = React.lazy(() => import('./modules/activity/presenter/PresenterWindow'));
const AuthSourceProvider = React.lazy(() => import('./modules/auth/components/auth-source/AuthSourceProvider'));
import { PRINT_ROUTE_PREFIX } from './modules/activity/renderers/print-renderer/printPath';
import { applyPrintApiOrigin } from './modules/activity/renderers/print-renderer/printApiOrigin';
import { LoadingPage } from './shared/components/ui';
import { ThemeProvider } from './shared/components/theme';
import './styles/index.css';

// Lazy so the print page's unscopable @page rule and the render tree stay out of every other page's bundle.
const PrintRoot = React.lazy(() => import('./modules/activity/renderers/print-renderer/PrintRoot'));

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

// Check if this is the presenter window
const isPresenterWindow = new URLSearchParams(window.location.search).get('presenter') === 'true';
const isPrintWindow = window.location.pathname.startsWith(PRINT_ROUTE_PREFIX);

if (isPresenterWindow) {
  // Render presenter window without authentication and routing
  root.render(
    <React.StrictMode>
      <Suspense fallback={<LoadingPage />}>
        <PresenterWindow />
      </Suspense>
    </React.StrictMode>
  );
} else if (isPrintWindow) {
  // The export worker's browser cannot load Clerk, so the print page boots outside it.
  applyPrintApiOrigin();
  root.render(
    <React.StrictMode>
      <Suspense fallback={null}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <ThemeProvider category="student">
              <PrintRoot />
            </ThemeProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </Suspense>
    </React.StrictMode>
  );
} else {
  root.render(
    <React.StrictMode>
      <Suspense fallback={<LoadingPage />}>
        <HelmetProvider>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <AuthSourceProvider>
                <App />
              </AuthSourceProvider>
            </BrowserRouter>
          </QueryClientProvider>
        </HelmetProvider>
      </Suspense>
    </React.StrictMode>
  );
}
